import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LuArrowUpDown, LuCheck, LuSearch, LuSlidersHorizontal, LuX } from "react-icons/lu";
import FilterChips from "../components/FilterChips";
import ProductCard from "../components/ProductCard";
import ShopHeader from "../components/ShopHeader";
import { EmptyState, ErrorState, ListSkeleton, ProductCardSkeleton } from "../components/States";
import StorePicker from "../components/StorePicker";
import { useShopProducts } from "../hooks/useShopProducts";
import { useShopStore } from "../hooks/useShopStore";
import { useFavorites, useToast } from "../hooks/useShopUi";
import { FILTER_LABELS, SORT_LABELS } from "../utils/scoreFormatter";
import "../shop.css";

/** Listing for one sub-category of one store ("Pulses & Dal · 120 Products"). */
export default function ProductListingScreen() {
  const navigate = useNavigate();
  const { subCategoryId } = useParams();
  const [params, setParams] = useSearchParams();
  const shopStore = useShopStore();
  const storeId = Number(params.get("store")) || shopStore.storeId;

  const filter = params.get("filter") ?? "ALL";
  const sort = params.get("sort") ?? "relevance";
  const [searchOpen, setSearchOpen] = useState(Boolean(params.get("q")));
  const [searchText, setSearchText] = useState(params.get("q") ?? "");
  const search = useDebounced(searchText.trim(), 300);
  const [menu, setMenu] = useState(null); // "sort" | "filter" | null

  const list = useShopProducts({ storeId, subCategoryId: Number(subCategoryId), filter, sort, search });
  const { isFavorite, toggle } = useFavorites();
  const { show, toast } = useToast();

  // Filter, sort and search live in the URL so Back returns to the same list.
  const setParam = useCallback(
    (key, value, fallback) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (!value || value === fallback) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  useEffect(() => setParam("q", search, ""), [search, setParam]);

  const openProduct = useCallback(
    (productId) => navigate(`/shop/product/${productId}?store=${storeId}`),
    [navigate, storeId],
  );
  const toggleFavorite = useCallback(
    (productId) => show(toggle(productId) ? "Added to favorites" : "Removed from favorites"),
    [toggle, show],
  );

  const sentinel = useInfiniteScroll(list.loadMore, list.hasMore);
  const title = list.subCategory?.name ?? "Products";
  const subtitle = list.status === "ready" ? `${list.total} Product${list.total === 1 ? "" : "s"}` : "Loading…";

  return (
    <div className="ep-shop">
      <ShopHeader
        title={title}
        subtitle={subtitle}
        onBack={() => navigate("/shop")}
        searchOpen={searchOpen}
        onSearch={() => {
          if (searchOpen) setSearchText("");
          setSearchOpen(!searchOpen);
        }}
      />

      {searchOpen && (
        <label className="ep-search">
          <LuSearch aria-hidden="true" />
          <span className="ep-visually-hidden">Search products</span>
          <input
            autoFocus
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={`Search in ${title}`}
          />
          {searchText && (
            <button type="button" className="ep-icon-btn" style={{ width: 30, height: 30, fontSize: 16 }} aria-label="Clear search" onClick={() => setSearchText("")}>
              <LuX />
            </button>
          )}
        </label>
      )}

      {!params.get("store") && <StorePicker stores={shopStore.stores} storeId={storeId} onChoose={shopStore.choose} />}

      <FilterChips
        filters={list.filters.length ? list.filters : Object.keys(FILTER_LABELS).map((code) => ({ code, count: 0 }))}
        selected={filter}
        onSelect={(code) => setParam("filter", code, "ALL")}
      />

      <div className="ep-toolbar">
        <button
          type="button"
          className={`ep-tool ${sort !== "relevance" ? "is-active" : ""}`}
          aria-haspopup="menu"
          aria-expanded={menu === "sort"}
          onClick={() => setMenu(menu === "sort" ? null : "sort")}
        >
          <LuArrowUpDown aria-hidden="true" /> Sort
        </button>
        <button
          type="button"
          className={`ep-tool ${filter !== "ALL" ? "is-active" : ""}`}
          aria-haspopup="menu"
          aria-expanded={menu === "filter"}
          onClick={() => setMenu(menu === "filter" ? null : "filter")}
        >
          <LuSlidersHorizontal aria-hidden="true" /> Filter
        </button>
        {sort !== "relevance" && <span className="ep-toolbar-meta">{SORT_LABELS[sort]}</span>}

        {menu && (
          <div className="ep-menu" role="menu" aria-label={menu === "sort" ? "Sort by" : "Filter by"}>
            {menu === "sort"
              ? Object.entries(SORT_LABELS).map(([code, label]) => (
                  <MenuItem key={code} selected={sort === code} onClick={() => { setParam("sort", code, "relevance"); setMenu(null); }}>
                    {label}
                  </MenuItem>
                ))
              : list.filters.map(({ code, count }) => (
                  <MenuItem key={code} selected={filter === code} onClick={() => { setParam("filter", code, "ALL"); setMenu(null); }}>
                    {FILTER_LABELS[code]} <small style={{ color: "var(--ep-muted)" }}>({count})</small>
                  </MenuItem>
                ))}
          </div>
        )}
      </div>

      {!storeId && shopStore.error ? (
        <ErrorState error={shopStore.error} title="Unable to load stores" onRetry={() => window.location.reload()} />
      ) : (
        list.status === "loading" && <ListSkeleton />
      )}
      {list.status === "error" && (
        <ErrorState error={list.error} title="Unable to load products" notFoundTitle="Store not found" onRetry={list.retry} />
      )}
      {list.status === "ready" && list.items.length === 0 && (
        <EmptyState
          title={search ? `No results for “${search}”` : "No products here yet"}
          message={
            search || filter !== "ALL"
              ? "Try another word or clear the filter."
              : "This store has no products in this category. Try another store."
          }
          action={
            (search || filter !== "ALL") && (
              <button type="button" className="ep-btn is-outline" onClick={() => { setSearchText(""); setParam("filter", "ALL", "ALL"); }}>
                Clear search and filter
              </button>
            )
          }
        />
      )}
      {list.status === "ready" && list.items.length > 0 && (
        <div className="ep-list">
          {list.items.map((product) => (
            <ProductCard
              key={product.productId}
              product={product}
              favorite={isFavorite(product.productId)}
              onOpen={openProduct}
              onToggleFavorite={toggleFavorite}
              onMessage={show}
            />
          ))}
          {list.loadingMore && (
            <>
              <ProductCardSkeleton />
              <ProductCardSkeleton />
            </>
          )}
          {list.error && !list.loadingMore && (
            <button type="button" className="ep-btn is-outline" style={{ justifySelf: "center" }} onClick={list.loadMore}>
              Couldn't load more — Retry
            </button>
          )}
        </div>
      )}
      <div ref={sentinel} className="ep-sentinel" aria-hidden="true" />
      {toast}
    </div>
  );
}

function MenuItem({ selected, onClick, children }) {
  return (
    <button type="button" role="menuitemradio" aria-checked={selected} className={`ep-menu-item ${selected ? "is-selected" : ""}`} onClick={onClick}>
      <span>{children}</span>
      {selected && <LuCheck aria-hidden="true" />}
    </button>
  );
}

function useDebounced(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Calls `onMore` when the sentinel scrolls into view. */
function useInfiniteScroll(onMore, enabled) {
  const ref = useRef(null);
  const latest = useRef(onMore);
  latest.current = onMore;
  useEffect(() => {
    if (!enabled || !ref.current || !("IntersectionObserver" in window)) return undefined;
    const observer = new IntersectionObserver((entries) => entries[0].isIntersecting && latest.current(), {
      rootMargin: "400px",
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [enabled]);
  return ref;
}
